import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  ChevronDown, ChevronRight, Loader2, Sparkles, Database, AlertTriangle,
  CheckCircle2, Eye, ArrowLeft, Wand2,
} from "lucide-react";

const TAX_OPTIONS = [
  { id: "none", label: "Sin impuesto" },
  { id: "iva", label: "IVA 21%" },
  { id: "igic", label: "IGIC 7%" },
];

function flattenLeafCodes(node, acc) {
  if (!node) return acc;
  if (node.es_capitulo) {
    var children = node.hijos || [];
    for (var i = 0; i < children.length; i++) flattenLeafCodes(children[i], acc);
  } else {
    acc.push(node.codigo);
  }
  return acc;
}

function renderTreeNode(node, depth, openMap, setOpenMap, selected, selectedChapters, toggleCode, toggleChapter, underSelChapter) {
  const code = node.codigo;
  const hijos = node.hijos || [];
  const open = openMap[code] !== false;
  const isCap = node.es_capitulo;
  const checked = isCap ? selectedChapters.has(code) : (selected.has(code) || underSelChapter);
  const childrenUnder = underSelChapter || (isCap && selectedChapters.has(code));
  return (
    <div key={code} className="ml-2" style={{ paddingLeft: depth * 8 }}>
      <div className={"flex items-center gap-1 hover:bg-slate-50 px-1 py-1 " + (isCap ? "font-semibold" : "")}>
        <button onClick={() => hijos.length && setOpenMap((m) => ({ ...m, [code]: !open }))} className="w-5">
          {hijos.length ? (open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />) : null}
        </button>
        <input
          type="checkbox"
          checked={checked}
          disabled={!isCap && underSelChapter}
          onChange={() => isCap ? toggleChapter(code) : toggleCode(code)}
          data-testid={"bc3-chk-" + code}
        />
        <span className="text-xs font-mono text-slate-500 ml-1">{code}</span>
        <span className="flex-1 text-sm ml-1 truncate">{node.titulo}</span>
        {!isCap && (
          <span className="text-xs text-slate-500 whitespace-nowrap">
            {(node.cantidad || 1)} {node.unidad || "ud"} · {(node.precio || 0).toFixed(2)}{"€"}
          </span>
        )}
      </div>
      {open && hijos.map((h) => renderTreeNode(h, depth + 1, openMap, setOpenMap, selected, selectedChapters, toggleCode, toggleChapter, childrenUnder))}
    </div>
  );
}

export default function Bc3Import() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const fileId = params.get("file_id");
  const [file, setFile] = useState(null);
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openMap, setOpenMap] = useState({});
  const [selectedCodes, setSelectedCodes] = useState(new Set());
  const [selectedChapters, setSelectedChapters] = useState(new Set());
  const [margin, setMargin] = useState(0);
  const [round2, setRound2] = useState(true);
  const [mergeDup, setMergeDup] = useState(false);
  const [preview, setPreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [creating, setCreating] = useState(false);
  const [clients, setClients] = useState([]);
  const [budgetForm, setBudgetForm] = useState({
    title: "",
    client_id: "",
    client_name: "",
    client_phone: "",
    client_email: "",
    client_address: "",
    client_type: "particular",
    tax_type: user?.default_tax_type || "iva",
  });

  useEffect(() => {
    if (!fileId) {
      toast.error("Falta file_id");
      nav("/files");
      return;
    }
    (async () => {
      try {
        const fr = await api.get("/files");
        const f = (fr.data || []).find((x) => x.id === fileId);
        setFile(f);
        if (!f) throw new Error("Archivo no encontrado");
        if (!f.bc3_data) {
          toast.info("Procesando BC3 primero…");
          const fd = new FormData();
          fd.append("file_id", fileId);
          const pr = await api.post("/bc3/process", fd, { headers: { "Content-Type": "multipart/form-data" } });
          setTree(pr.data.tree);
          setBudgetForm((s) => ({ ...s, title: f.display_name || f.name }));
        } else {
          setTree(f.bc3_data);
          setBudgetForm((s) => ({ ...s, title: f.display_name || f.name }));
        }
        const cr = await api.get("/clients");
        setClients(cr.data || []);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Error cargando BC3");
      } finally { setLoading(false); }
    })();
  }, [fileId]);

  const toggleCode = (code) => {
    setSelectedCodes((s) => {
      const ns = new Set(s);
      ns.has(code) ? ns.delete(code) : ns.add(code);
      return ns;
    });
  };

  const toggleChapter = (code) => {
    setSelectedChapters((s) => {
      const ns = new Set(s);
      ns.has(code) ? ns.delete(code) : ns.add(code);
      return ns;
    });
  };

  const selectAll = () => {
    if (!tree) return;
    const all = new Set();
    tree.capitulos.forEach((c) => flattenLeafCodes(c, []).forEach((x) => all.add(x)));
    setSelectedCodes(all);
    setSelectedChapters(new Set());
  };

  const clearSel = () => {
    setSelectedCodes(new Set());
    setSelectedChapters(new Set());
  };

  const onClient = (e) => {
    const id = e.target.value;
    if (!id) return setBudgetForm({ ...budgetForm, client_id: "", client_name: "", client_phone: "", client_email: "", client_address: "" });
    const c = clients.find((x) => x.id === id);
    if (c) setBudgetForm({ ...budgetForm, client_id: c.id, client_name: c.name, client_phone: c.phone, client_email: c.email, client_address: c.address });
  };

  const totalLeaves = useMemo(() => {
    if (!tree) return 0;
    let n = 0;
    tree.capitulos.forEach((c) => { n += flattenLeafCodes(c, []).length; });
    return n;
  }, [tree]);

  const buildPayload = () => ({
    file_id: fileId,
    selected_codes: Array.from(selectedCodes),
    selected_chapters: Array.from(selectedChapters),
    margin_pct: parseFloat(margin) || 0,
    round_2dec: !!round2,
    merge_duplicates: !!mergeDup,
  });

  const doPreview = async () => {
    try {
      const r = await api.post("/bc3/import-to-budget", buildPayload());
      setPreview(r.data);
      toast.success(`Vista previa: ${r.data.count} partidas`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error vista previa");
    }
  };

  const doAnalyze = async () => {
    setAnalyzing(true);
    try {
      const r = await api.post("/bc3/analyze", { file_id: fileId, sample_size: 25 });
      setAnalysis(r.data);
      toast.success(`Analizadas ${r.data.sampled} partidas con IA`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error análisis IA");
    } finally { setAnalyzing(false); }
  };

  const createBudget = async () => {
    if (!budgetForm.client_name) return toast.error("Indica el cliente");
    setCreating(true);
    try {
      const payload = { ...buildPayload(), ...budgetForm };
      const r = await api.post("/budgets/from-bc3", payload);
      toast.success("Presupuesto creado · revísalo y envíalo");
      nav(`/budgets/${r.data.id}/edit`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error creando presupuesto");
    } finally { setCreating(false); }
  };

  if (loading) {
    return <div className="p-8 text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline" /> Cargando árbol BC3…</div>;
  }
  if (!tree) return <div className="p-8 text-red-600">No se ha podido cargar el BC3.</div>;

  const validation = file?.bc3_validation;

  return (
    <div className="space-y-6" data-testid="bc3-import-page">
      <div className="flex items-center gap-2">
        <button onClick={() => nav("/files")} className="p-2 hover:bg-slate-100"><ArrowLeft className="w-4 h-4" /></button>
        <h1 className="text-2xl sm:text-3xl font-display font-extrabold tracking-tight">Importar BC3 → Presupuesto</h1>
      </div>
      <div className="text-sm text-slate-500">{file?.display_name || file?.name} · {totalLeaves} partidas · {tree.capitulos.length} capítulos</div>

      {validation && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div className={`p-3 border ${validation.hierarchy?.ok ? "bg-green-50 border-green-200" : "bg-yellow-50 border-yellow-200"}`}>
            <div className="text-xs font-semibold flex items-center gap-1">
              {validation.hierarchy?.ok ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <AlertTriangle className="w-4 h-4 text-yellow-600" />}
              Validación jerarquía
            </div>
            <div className="text-xs mt-1">
              {validation.hierarchy?.capitulos} capítulos · {validation.hierarchy?.leaves} partidas
              {!validation.hierarchy?.ok && <span> · {validation.hierarchy.issues?.length} avisos</span>}
            </div>
          </div>
          <div className={`p-3 border ${validation.duplicates?.length ? "bg-yellow-50 border-yellow-200" : "bg-green-50 border-green-200"}`}>
            <div className="text-xs font-semibold flex items-center gap-1">
              {validation.duplicates?.length ? <AlertTriangle className="w-4 h-4 text-yellow-600" /> : <CheckCircle2 className="w-4 h-4 text-green-600" />}
              Duplicados
            </div>
            <div className="text-xs mt-1">{validation.duplicates?.length || 0} grupos detectados</div>
          </div>
          <button
            data-testid="btn-bc3-analyze"
            onClick={doAnalyze}
            disabled={analyzing}
            className="p-3 border bg-orange-50 border-orange-200 text-left hover:bg-orange-100"
          >
            <div className="text-xs font-semibold flex items-center gap-1">
              <Wand2 className="w-4 h-4 text-orange-600" /> Análisis IA
            </div>
            <div className="text-xs mt-1">{analyzing ? "Analizando…" : "Sugerir precios actualizados"}</div>
          </button>
        </div>
      )}

      {analysis?.suggestions?.length > 0 && (
        <div className="bg-white border border-slate-200 p-4">
          <div className="font-semibold flex items-center gap-2 mb-2"><Sparkles className="w-4 h-4 text-orange-600" /> Sugerencias IA</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-2">Código</th>
                  <th className="text-left p-2">Concepto</th>
                  <th className="text-right p-2">Actual</th>
                  <th className="text-right p-2">Mín-Máx</th>
                  <th className="text-left p-2">Comentario</th>
                </tr>
              </thead>
              <tbody>
                {analysis.suggestions.slice(0, 30).map((s, i) => (
                  <tr key={i} className={`border-t border-slate-100 ${s.obsoleto ? "bg-yellow-50" : ""}`}>
                    <td className="p-2 font-mono">{s.codigo}</td>
                    <td className="p-2 truncate max-w-[200px]">{s.concepto}</td>
                    <td className="p-2 text-right">{(s.precio_actual || 0).toFixed(2)}€</td>
                    <td className="p-2 text-right">{(s.precio_min || 0).toFixed(2)} – {(s.precio_max || 0).toFixed(2)}€</td>
                    <td className="p-2 text-slate-600">{s.comentario}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
        {/* Tree */}
        <div className="bg-white p-4 lg:col-span-2">
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold">Árbol BC3</div>
            <div className="flex gap-2 text-xs">
              <button onClick={selectAll} className="px-2 py-1 bg-slate-100 hover:bg-slate-200" data-testid="bc3-select-all">Seleccionar todo</button>
              <button onClick={clearSel} className="px-2 py-1 bg-slate-100 hover:bg-slate-200" data-testid="bc3-clear">Limpiar</button>
            </div>
          </div>
          <div className="max-h-[60vh] overflow-auto bg-slate-50 p-2 border border-slate-200" data-testid="bc3-tree">
            {tree.capitulos.map((c) => renderTreeNode(c, 0, openMap, setOpenMap, selectedCodes, selectedChapters, toggleCode, toggleChapter, false))}
          </div>
          <div className="text-xs text-slate-500 mt-2">
            {selectedChapters.size} capítulos · {selectedCodes.size} partidas seleccionadas
          </div>
        </div>

        {/* Right: options */}
        <div className="bg-white p-4 space-y-4">
          <div>
            <div className="font-semibold mb-2">Opciones</div>
            <label className="block text-xs text-slate-500">Margen %</label>
            <input
              type="number"
              data-testid="bc3-margin"
              value={margin}
              onChange={(e) => setMargin(e.target.value)}
              className="w-full border border-slate-300 px-3 py-2 text-sm"
            />
            <label className="flex items-center gap-2 mt-2 text-sm">
              <input type="checkbox" checked={round2} onChange={(e) => setRound2(e.target.checked)} data-testid="bc3-round" />
              Redondear precios a 2 decimales
            </label>
            <label className="flex items-center gap-2 mt-1 text-sm">
              <input type="checkbox" checked={mergeDup} onChange={(e) => setMergeDup(e.target.checked)} data-testid="bc3-merge" />
              Fusionar partidas repetidas
            </label>
          </div>

          <div>
            <div className="font-semibold mb-2">Cliente</div>
            {clients.length > 0 && (
              <select onChange={onClient} value={budgetForm.client_id} className="w-full border border-slate-300 px-3 py-2 text-sm mb-2" data-testid="bc3-client-select">
                <option value="">— Cliente nuevo —</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
            <input
              data-testid="bc3-client-name"
              value={budgetForm.client_name}
              onChange={(e) => setBudgetForm({ ...budgetForm, client_name: e.target.value })}
              placeholder="Nombre cliente"
              className="w-full border border-slate-300 px-3 py-2 text-sm mb-2"
            />
            <input
              value={budgetForm.client_email}
              onChange={(e) => setBudgetForm({ ...budgetForm, client_email: e.target.value })}
              placeholder="Email"
              className="w-full border border-slate-300 px-3 py-2 text-sm"
            />
          </div>

          <div>
            <div className="font-semibold mb-2">Detalles</div>
            <input
              value={budgetForm.title}
              onChange={(e) => setBudgetForm({ ...budgetForm, title: e.target.value })}
              placeholder="Título del presupuesto"
              className="w-full border border-slate-300 px-3 py-2 text-sm mb-2"
              data-testid="bc3-title"
            />
            <select
              value={budgetForm.tax_type}
              onChange={(e) => setBudgetForm({ ...budgetForm, tax_type: e.target.value })}
              className="w-full border border-slate-300 px-3 py-2 text-sm"
              data-testid="bc3-tax"
            >
              {TAX_OPTIONS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>

          <div className="flex gap-2 flex-wrap">
            <button
              onClick={doPreview}
              data-testid="bc3-preview"
              className="flex-1 bg-slate-200 px-3 py-2 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-slate-300"
            >
              <Eye className="w-4 h-4" /> Vista previa
            </button>
            <button
              onClick={createBudget}
              data-testid="bc3-create"
              disabled={creating}
              className="flex-1 bg-orange-600 text-white px-3 py-2 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-orange-700 disabled:opacity-50"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
              Crear presupuesto
            </button>
          </div>
        </div>
      </div>

      {preview && (
        <div className="bg-white border border-slate-200 p-4" data-testid="bc3-preview-result">
          <div className="font-semibold mb-2">Vista previa · {preview.count} partidas</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-2">Concepto</th>
                  <th className="text-right p-2">Cant.</th>
                  <th className="text-left p-2">Unidad</th>
                  <th className="text-right p-2">Precio</th>
                  <th className="text-right p-2">Total</th>
                </tr>
              </thead>
              <tbody>
                {preview.items.slice(0, 50).map((it, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="p-2 truncate max-w-[300px]">{it.concept}</td>
                    <td className="p-2 text-right">{it.quantity}</td>
                    <td className="p-2">{it.unit}</td>
                    <td className="p-2 text-right">{(it.unit_price || 0).toFixed(2)}€</td>
                    <td className="p-2 text-right font-semibold">{((it.quantity || 0) * (it.unit_price || 0)).toFixed(2)}€</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {preview.items.length > 50 && (
            <div className="text-xs text-slate-500 mt-1">… y {preview.items.length - 50} más.</div>
          )}
        </div>
      )}
    </div>
  );
}
